<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-slate-900 text-white py-20 px-4">
      <div class="max-w-6xl mx-auto text-center">
        <h1 class="text-4xl md:text-5xl font-bold mb-6">
          GVGMall — Digital Store for Games, Software and Online Licenses
        </h1>
        <p class="text-lg md:text-xl text-slate-300 mb-10 max-w-3xl mx-auto">
          GVGMall is an online store focused on digital products, offering games, software, licenses and online solutions with clear information, digital delivery and customer support.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="mailto:support@gvgmall.co" class="bg-slate-700 hover:bg-slate-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
            Contact Support
          </a>
        </div>
      </div>
    </section>

    <!-- Trust Cards -->
    <section class="py-16 px-4 bg-slate-50">
      <div class="max-w-6xl mx-auto">
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-slate-900 mb-2">Digital Delivery</h3>
            <p class="text-slate-600">Products are delivered electronically via email or customer account area.</p>
          </div>
          <div class="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-slate-900 mb-2">Customer Support</h3>
            <p class="text-slate-600">Our team is available to assist with questions and support requests.</p>
          </div>
          <div class="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-slate-900 mb-2">Transparent Information</h3>
            <p class="text-slate-600">Clear product details, delivery information and policies.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About GVGMall -->
    <section class="py-16 px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-6 text-center">About GVGMall</h2>
        <div class="prose prose-lg max-w-none text-slate-600 space-y-4">
          <p>
            GVGMall is an online digital store that provides access to digital products such as games, software, licenses and related online solutions. Our goal is to make the digital purchase experience simple, clear and reliable.
          </p>
          <p>
            We focus on providing product information, delivery details, support channels and commercial policies so customers can make informed purchase decisions before placing an order.
          </p>
        </div>
      </div>
    </section>

    <!-- What We Offer -->
    <section class="py-16 px-4 bg-slate-50">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-8 text-center">What We Offer</h2>
        <div class="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
          <ul class="space-y-3 text-slate-600">
            <li class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              Digital games
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              Software for personal and professional use
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              Digital licenses
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              PC-compatible digital products
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              Online solutions with customer support
            </li>
          </ul>
          <p class="mt-6 text-sm text-slate-500 italic">
            Each product page may include specific information about compatibility, platform, region restrictions, delivery method and usage requirements.
          </p>
        </div>
      </div>
    </section>

    <!-- How the Purchase Works -->
    <section class="py-16 px-4">
      <div class="max-w-6xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-12 text-center">How the Purchase Works</h2>
        <div class="grid md:grid-cols-5 gap-6">
          <div v-for="(step, index) in purchaseSteps" :key="index" class="text-center">
            <div class="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              {{ index + 1 }}
            </div>
            <h3 class="font-semibold text-slate-900 mb-2">{{ step.title }}</h3>
            <p class="text-sm text-slate-600">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Digital Delivery -->
    <section class="py-16 px-4 bg-slate-50">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-6 text-center">Digital Delivery</h2>
        <div class="prose prose-lg max-w-none text-slate-600 space-y-4">
          <p>
            Most products sold by GVGMall are delivered digitally. Depending on the product, delivery may occur by email, customer account area or another electronic method described during the purchase process.
          </p>
          <p>
            Delivery time may vary according to payment confirmation, product availability, security verification and the specific conditions described on the product page.
          </p>
        </div>
      </div>
    </section>

    <!-- Customer Support -->
    <section class="py-16 px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-6 text-center">Customer Support</h2>
        <div class="prose prose-lg max-w-none text-slate-600 space-y-4 mb-8">
          <p>
            Our support team can assist customers with pre-sale questions, order status, product access, activation instructions, installation guidance and general support requests.
          </p>
        </div>
        <div class="bg-slate-50 p-6 rounded-xl border border-slate-200">
          <div class="space-y-3">
            <div class="flex items-center">
              <svg class="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span class="text-slate-700"><strong>Email:</strong> <a href="mailto:support@gvgmall.co" class="text-blue-600 hover:underline">support@gvgmall.co</a></span>
            </div>
            <div class="flex items-center">
              <svg class="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-slate-700"><strong>Business hours:</strong> Monday to Friday, 9 AM to 6 PM</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Transparency and Safety -->
    <section class="py-16 px-4 bg-slate-50">
      <div class="max-w-6xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-8 text-center">Transparency and Safety</h2>
        <div class="grid md:grid-cols-3 gap-6 mb-8">
          <div v-for="(item, index) in transparencyCards" :key="index" class="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 class="font-semibold text-slate-900 mb-2">{{ item }}</h3>
          </div>
        </div>
        <div class="max-w-4xl mx-auto prose prose-lg max-w-none text-slate-600">
          <p>
            Before purchasing, customers should review product compatibility, activation platform, region limitations, delivery time, refund conditions and technical requirements.
          </p>
        </div>
      </div>
    </section>

    <!-- Important Policies -->
    <section class="py-16 px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-8 text-center">Important Policies</h2>
        <div class="grid md:grid-cols-2 gap-4">
          <NuxtLink
            v-for="policy in policies"
            :key="policy.path"
            :to="policy.path"
            class="bg-slate-50 hover:bg-slate-100 p-6 rounded-xl border border-slate-200 transition-colors"
          >
            <div class="flex items-center">
              <svg class="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="font-semibold text-slate-900">{{ policy.label }}</span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Business Information -->
    <section class="py-16 px-4 bg-slate-50">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-8 text-center">Business Information</h2>
        <div class="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <p class="text-sm text-slate-500 mb-1">Commercial name</p>
              <p class="font-semibold text-slate-900">GVGMall</p>
            </div>
            <div>
              <p class="text-sm text-slate-500 mb-1">Website</p>
              <p class="font-semibold text-slate-900">gvgmall.co</p>
            </div>
            <div>
              <p class="text-sm text-slate-500 mb-1">Support email</p>
              <p class="font-semibold text-slate-900">support@gvgmall.co</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-16 px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
        <div class="space-y-4">
          <div v-for="(faq, index) in faqs" :key="index" class="bg-slate-50 p-6 rounded-xl border border-slate-200">
            <h3 class="font-semibold text-slate-900 mb-2">{{ faq.question }}</h3>
            <p class="text-slate-600">{{ faq.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Disclaimer -->
    <section class="py-12 px-4 bg-slate-100">
      <div class="max-w-4xl mx-auto">
        <div class="bg-white p-6 rounded-xl border border-slate-200">
          <p class="text-sm text-slate-600 text-center">
            <strong>Trademark Disclaimer:</strong> Unless clearly stated with formal authorization, GVGMall is not an official representative of third-party brands. All trademarks belong to their respective owners.
          </p>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="py-20 px-4 bg-slate-900 text-white">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-3xl font-bold mb-4">Learn more about GVGMall</h2>
        <p class="text-lg text-slate-300 mb-10">
          Visit our store, review product information carefully and contact our support team if you have any questions before purchasing.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="mailto:support@gvgmall.co" class="bg-slate-700 hover:bg-slate-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
            Contact Support
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: false
})

const purchaseSteps = [
  { title: 'Choose a product', description: 'Browse our catalog and select the product you need' },
  { title: 'Review details', description: 'Check compatibility, platform and delivery information' },
  { title: 'Complete order', description: 'Proceed to checkout and complete your purchase' },
  { title: 'Digital delivery', description: 'Receive your product via email or account area' },
  { title: 'Support if needed', description: 'Contact our team for any post-purchase assistance' }
]

const transparencyCards = [
  'Product descriptions',
  'Digital delivery information',
  'Refund policy',
  'Contact channels',
  'Customer support',
  'Terms and privacy information'
]

const policies = [
  { path: '/privacy-policy', label: 'Privacy Policy' },
  { path: '/terms-of-service', label: 'Terms of Service' },
  { path: '/refund-policy', label: 'Refund Policy' },
  { path: '/digital-delivery-policy', label: 'Digital Delivery Policy' },
  { path: '/contact', label: 'Contact' }
]

const faqs = [
  {
    question: 'Does GVGMall sell physical products?',
    answer: 'GVGMall mainly works with digital products. If a product has a different delivery method, this information will be clearly described on the product page.'
  },
  {
    question: 'How do I receive my product?',
    answer: 'Products are delivered digitally by email, customer account area or another electronic method described during the purchase process.'
  },
  {
    question: 'Is delivery instant?',
    answer: 'Some products may be delivered quickly or automatically, while others may depend on payment confirmation, security verification or availability.'
  },
  {
    question: 'Can I contact support after purchase?',
    answer: 'Yes. Customers can contact support for help with order status, access, activation, installation or initial usage guidance.'
  },
  {
    question: 'Can I request a refund?',
    answer: 'Refund eligibility depends on the type of digital product and the conditions described in the Refund Policy.'
  },
  {
    question: 'Is GVGMall affiliated with software or gaming brands?',
    answer: 'Unless clearly stated with formal authorization, GVGMall is not an official representative of third-party brands. All trademarks belong to their respective owners.'
  }
]

useSeoMeta({
  title: 'About GVGMall | Digital Store for Games, Software and Licenses',
  description: 'Learn more about GVGMall, a digital store offering games, software, digital licenses, online delivery and customer support with transparent information.'
})
</script>
